import { Injectable } from '@angular/core';
import { EMPTY, Observable, from, of, throwError } from 'rxjs';
import { concatMap, expand, last, map, switchMap, toArray } from 'rxjs/operators';
import _chunk from 'lodash/chunk';
import {
  BATCH_LIMITS,
  BatchLimit,
  DEFAULT_PAGE_NUM,
  DEFAULT_PAGE_SIZE,
  PAGE_LIMITS,
  PageLimit,
} from '../../constants/pagination.constants';
import { RequestLimitExceededError } from './request-limit.error';

export interface PagedPayload<T> {
  data?: T[];
  totalCount?: number;
}

interface PageCursor<T> {
  num: number;
  items: T[];
  totalCount: number;
  complete: boolean;
  truncated: boolean;
}

@Injectable({ providedIn: 'root' })
export class PaginationService {
  pageSize(limit: PageLimit): number {
    return PAGE_LIMITS[limit].size;
  }

  maxOffset(limit: PageLimit): number {
    return PAGE_LIMITS[limit].maxOffset;
  }

  batchSize(limit: BatchLimit): number {
    return BATCH_LIMITS[limit];
  }

  pageOffset(num: number, size: number): number {
    return (num - DEFAULT_PAGE_NUM) * size;
  }

  maxPageNum(maxOffset: number, size: number): number {
    return Math.floor(maxOffset / size) + DEFAULT_PAGE_NUM;
  }

  clampPageSize(size: number, maxSize: number): number {
    return this.isUsablePositive(size) ? Math.min(Math.floor(size), maxSize) : Math.min(DEFAULT_PAGE_SIZE, maxSize);
  }

  clampPageNum(num: number, size: number, maxOffset: number): number {
    return this.isUsablePositive(num) ? Math.min(Math.floor(num), this.maxPageNum(maxOffset, size)) : DEFAULT_PAGE_NUM;
  }

  loadAllPages<T>(
    loadPage: (num: number, size: number) => Observable<PagedPayload<T>>,
    limit: PageLimit = PageLimit.Large,
    size: number = this.pageSize(limit),
  ): Observable<T[]> {
    const maxPageNum = this.maxPageNum(this.maxOffset(limit), size);

    return of(this.createCursor<T>()).pipe(
      expand(cursor =>
        cursor.complete
          ? EMPTY
          : loadPage(cursor.num + DEFAULT_PAGE_NUM, size).pipe(
              map(page => this.advanceCursor(cursor, page, size, maxPageNum)),
            ),
      ),
      last(),
      switchMap(cursor =>
        cursor.truncated
          ? throwError(() => new RequestLimitExceededError(cursor.items.length, cursor.totalCount))
          : of(cursor.items),
      ),
    );
  }

  runInBatches<TId, TResult>(
    ids: TId[],
    run: (batch: TId[]) => Observable<TResult>,
    batchSize: number = this.batchSize(BatchLimit.Batch),
  ): Observable<TResult[]> {
    return ids?.length
      ? from(_chunk(ids, batchSize)).pipe(
          concatMap(batch => run(batch)),
          toArray(),
        )
      : of([]);
  }

  loadInBatches<TId, TItem>(
    ids: TId[],
    loadBatch: (batch: TId[]) => Observable<TItem[]>,
    batchSize: number = this.batchSize(BatchLimit.Batch),
  ): Observable<TItem[]> {
    return this.runInBatches(ids, loadBatch, batchSize).pipe(map(batches => batches.flat()));
  }

  private createCursor<T>(): PageCursor<T> {
    return { num: 0, items: [], totalCount: 0, complete: false, truncated: false };
  }

  private advanceCursor<T>(
    cursor: PageCursor<T>,
    page: PagedPayload<T>,
    size: number,
    maxPageNum: number,
  ): PageCursor<T> {
    const data = page?.data ?? [];
    const items = cursor.items.concat(data);
    const num = cursor.num + 1;
    const totalCount = page?.totalCount ?? items.length;
    const pageWasFull = data.length >= size;
    const knowsMoreRemain = page?.totalCount === undefined || items.length < totalCount;
    const hasMore = pageWasFull && knowsMoreRemain;
    const reachedCeiling = num >= maxPageNum;

    return { num, items, totalCount, complete: !hasMore || reachedCeiling, truncated: hasMore && reachedCeiling };
  }

  private isUsablePositive(value: number): boolean {
    return Number.isFinite(value) && value > 0;
  }
}
